import { addDays, isAfter, parse } from 'date-fns';

const HAS_TIMEZONE = /[zZ]|[+-]\d{2}:\d{2}$/;

export const dateAtTime = (day: Date, time: string): Date | null => {
  const trimmed = time.trim();
  if (!trimmed) return null;
  const parsed = parse(
    trimmed,
    trimmed.split(':').length > 2 ? 'HH:mm:ss' : 'HH:mm',
    day
  );
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const endOnOrAfterStart = (start: Date, end: Date): Date =>
  isAfter(end, start) ? end : addDays(end, 1);

export const availabilityLocalTimestamp = (date: Date): string => {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
    date.getSeconds()
  )}`;
};

export const isValidDate = (value: Date | null | undefined): value is Date =>
  !!value && !Number.isNaN(value.getTime());

export const toEpochMs = (value: string | Date): number => {
  if (value instanceof Date) return value.getTime();
  const normalized = HAS_TIMEZONE.test(value) ? value : `${value}Z`;
  return new Date(normalized).getTime();
};

export const parseUtcDate = (value: string | Date): Date =>
  new Date(toEpochMs(value));

export const formatDateToNow = (value: string | Date | undefined): string => {
  if (!value) return '';
  const then = toEpochMs(value);
  if (Number.isNaN(then)) return '';

  const diff = Date.now() - then;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(then).toLocaleDateString();
};

export const formatDate = (
  value: string | Date | undefined,
  showTime = false,
  showMonthName = false
): string => {
  if (!value) return '';

  const ms = toEpochMs(value);
  if (Number.isNaN(ms)) {
    return String(value);
  }

  const date = new Date(ms);
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    day: 'numeric',
    month: showMonthName ? 'short' : 'numeric',
  };

  if (showTime) {
    options.hour = 'numeric';
    options.minute = '2-digit';
  }

  return date.toLocaleString(undefined, options);
};

/** e.g. "Thursday 10, Sep 26 1:30pm" */
export const formatStatusHistoryDate = (
  value: string | Date | undefined
): string => {
  if (!value) return '';
  const ms = toEpochMs(value);
  if (Number.isNaN(ms)) return String(value);

  const date = new Date(ms);
  const weekday = date.toLocaleDateString('en-US', { weekday: 'long' });
  const day = date.toLocaleDateString('en-US', { day: 'numeric' });
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const year = date.toLocaleDateString('en-US', { year: '2-digit' });
  const time = date
    .toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    .replace(/[\s\u00A0\u202F]/g, '')
    .replace(/\./g, '')
    .toLowerCase();

  return `${weekday} ${day}, ${month} ${year} ${time}`;
};
